import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Transaction } from "../../../../../app/entities/Transaction";
import { formatMoneyValue } from "../../../../../app/utils/formatMoneyValue";
import { onlyFirstName } from "@/app/utils/onlyFirstName";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import Status from "@/view/components/Status";
import { Button } from "@/view/components/ui/button";

interface TransactionsListProps {
  transactions: Transaction[];
  isLoading: boolean;
  getPageCount?: () => number;
  getPageIndex?: () => number;
  handleNextPage?: () => void;
  handlePreviousPage?: () => void;
}

interface GroupedTransactions {
  [key: string]: Transaction[];
}

export const TransactionsList = ({
  transactions,
  isLoading,
  getPageCount,
  getPageIndex,
  handleNextPage,
  handlePreviousPage,
}: TransactionsListProps) => {
  const groupedTransactions = transactions.reduce(
    (groups: GroupedTransactions, transaction) => {
      const date = format(new Date(transaction.createdAt), "yyyy-MM-dd");
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(transaction);
      return groups;
    },
    {},
  );

  const sortedDates = Object.keys(groupedTransactions).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime(),
  );

  const formatDateHeader = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `HOJE ${format(date, "MMM, dd", { locale: ptBR }).toUpperCase()}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `ONTEM ${format(date, "MMM, dd", { locale: ptBR }).toUpperCase()}`;
    } else {
      return format(date, "dd MMM, yyyy", { locale: ptBR }).toUpperCase();
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <div className="animate-pulse">
          <div className="mb-4 h-6 w-1/3 rounded bg-gray-200"></div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center space-x-4 py-3">
              <div className="h-10 w-10 rounded-full bg-gray-200"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 w-1/4 rounded bg-gray-200"></div>
                <div className="h-3 w-1/6 rounded bg-gray-200"></div>
              </div>
              <div className="space-y-1 text-right">
                <div className="h-4 w-20 rounded bg-gray-200"></div>
                <div className="h-3 w-16 rounded bg-gray-200"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="p-6 py-8 text-center">
        <p className="text-gray-500">
          Nenhuma transação encontrada para este link.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* <h3 className="mb-8 text-lg font-semibold text-black01">
        Solicitações realizadas com este link
      </h3> */}

      <div className="mt-4 space-y-8">
        {sortedDates.map((date) => (
          <div key={date} className="space-y-4">
            <div className="px-2 text-sm font-medium text-black04">
              {formatDateHeader(date)}
            </div>

            <div className="space-y-0">
              {groupedTransactions[date].map((transaction, index) => (
                <div key={transaction._id} className="relative">
                  {index > 0 && (
                    <div className="absolute left-0 right-0 top-0 h-px border-dashed bg-gray01"></div>
                  )}

                  <div className="px-2 py-5">
                    <div className="flex items-start space-x-4">
                      <div className="flex items-center justify-center">
                        <Status status={transaction.status} onlyIcon={true} />
                      </div>

                      <div className="flex-1">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-base font-medium text-black01">
                            {onlyFirstName(transaction.customer?.name) ||
                              "Cliente"}
                          </span>

                          <div className="mx-4 flex-1">
                            <div className="h-px border-t border-dashed border-gray01"></div>
                          </div>

                          <div className="text-base font-medium text-black01">
                            {formatMoneyValue(transaction.value)}
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2 text-sm text-black04">
                            <Clock size={14} />
                            <span>
                              {format(new Date(transaction.createdAt), "HH:mm")}
                            </span>
                          </div>

                          <div className="text-sm text-black04">
                            {formatMoneyValue(transaction.commission)} (
                            {(
                              (transaction.commission * 100) /
                              transaction.value
                            ).toFixed(2)}
                            %)
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {getPageIndex &&
          getPageCount &&
          handleNextPage &&
          handlePreviousPage && (
            <div className="mt-4 flex items-center justify-between gap-2 sm:justify-end sm:gap-4">
              <div className="flex items-center gap-1 text-xs text-gray-500 sm:hidden">
                <span>Página</span>
                <span className="font-medium text-gray-900">
                  {getPageIndex()}
                </span>
                <span>de</span>
                <span className="font-medium text-gray-900">
                  {getPageCount() || 0}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={getPageIndex() <= 1}
                  className="h-8 w-8 p-0 sm:h-9 sm:w-9"
                >
                  <ChevronLeft className="size-3 sm:size-4" />
                </Button>
                <div className="hidden items-center gap-2 text-sm sm:flex">
                  <span className="text-gray-500">Página</span>
                  <span className="font-medium">{getPageIndex()}</span>
                  <span className="text-gray-500">de</span>
                  <span className="font-medium">{getPageCount() || 0}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={getPageIndex() >= (getPageCount() || 0)}
                  className="h-8 w-8 p-0 sm:h-9 sm:w-9"
                >
                  <ChevronRight className="size-3 sm:size-4" />
                </Button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};
